import { useEffect } from "react";

import {
  Box,
  Button,
  Center,
  Heading,
  Inline,
  makeBox,
  Stack,
  useTheme
} from "@seneca/senekit";

import { useSimpleNavigationCallback } from "seneca-common/utils/hooks/routing";
import { useSelector } from "seneca-common/utils/hooks/selectors";
import { logMessage } from "seneca-common/utils/sentry/logError";

import useHideUpdateToastWhileMounted from "features/app/hooks/useHideUpdateToastWhileMounted";
import useUpdateApp from "features/app/hooks/useUpdateApp";
import { isLocalizeLoaded } from "features/common/localisation/utils";
import LoadingSpinner from "features/common/spinners/LoadingSpinner";
import { getAppVersion } from "features/common/utils/functions/app";
import { useIsPlurallApp } from "features/plurall";
import { getRegion } from "features/regions/selectors";

import { ReactComponent as plurallLogo } from "../assets/plurallLogo.svg";
import { ReactComponent as senecaGoStudentLogo } from "../assets/senecaGoStudentLogo.svg";
import { ReactComponent as senecaLogo } from "../assets/senecaLogo.svg";
import useHasWaitedXSecondsForNewVersion from "../hooks/useHasWaitedXSecondsForNewVersion";
import {
  getUpdatedBeforeReloadFlag,
  removeUpdatedBeforeReloadFlag,
  setUpdatedBeforeReloadFlag
} from "../utils";

const SenecaLogo = makeBox(senecaLogo);
const PlurallLogo = makeBox(plurallLogo);
const SenecaGoStudentLogo = makeBox(senecaGoStudentLogo);

export default function UpdateAppPage() {
  useHideUpdateToastWhileMounted();

  const { update, ready } = useUpdateApp();
  const { hasWaitedXSecondsForNewVersion } =
    useHasWaitedXSecondsForNewVersion(5);

  const isPlurallApp = useIsPlurallApp();
  const region = useSelector(getRegion);
  const showGoStudentLogo = (region && region !== "GB") || isLocalizeLoaded();

  const { foreground } = useTheme();

  const SplashLogo = isPlurallApp
    ? PlurallLogo
    : showGoStudentLogo
    ? SenecaGoStudentLogo
    : SenecaLogo;

  const loadingText = ready ? "Updating..." : "Checking for updates...";

  const updatedBeforeReload = getUpdatedBeforeReloadFlag();

  useEffect(() => {
    if (hasWaitedXSecondsForNewVersion && ready) {
      setUpdatedBeforeReloadFlag();
      update();
    }
  }, [ready, update, hasWaitedXSecondsForNewVersion]);

  const navigateToHome = useSimpleNavigationCallback("/");
  const redirectHandler = () => {
    logMessage("Update page flow completed", {
      extraInfo: {
        updateFound: updatedBeforeReload,
        appVersion: getAppVersion()
      }
    });
    removeUpdatedBeforeReloadFlag();
    navigateToHome();
  };

  const isUpdated =
    (updatedBeforeReload && !ready) ||
    (hasWaitedXSecondsForNewVersion && !ready);

  return (
    <Box
      position="fixed"
      width="100%"
      height="100%"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      background={foreground}
    >
      <Box
        display={["flex", "block"]}
        flexDirection="column"
        justifyContent="space-between"
        height={["100%", "none"]}
        paddingTop={["xxlarge", "none"]}
        paddingBottom="xlarge"
      >
        <Stack align="center">
          <SplashLogo width={["80%", ""]} />

          {isUpdated ? (
            <Heading align="center" level="3">
              You're all up to date!
            </Heading>
          ) : (
            <>
              <Inline alignItems="center" space="small">
                <Heading align="center" level="3">
                  {loadingText}
                </Heading>
                <LoadingSpinner loading colour="dark" size="xsmall" />
              </Inline>
            </>
          )}
        </Stack>

        {isUpdated && (
          <Box paddingTop="xxlarge">
            <Center>
              <Button width={["100%", "240px"]} onClick={redirectHandler}>
                Go back to Seneca
              </Button>
            </Center>
          </Box>
        )}
      </Box>
    </Box>
  );
}
